import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";

/**
 * Panel inferior para filtros y confirmaciones (sección 8.4).
 *
 * POR QUÉ SUBE DESDE ABAJO Y NO SE CENTRA
 *   El caso principal son 360-430 px y el pulgar vive en la mitad inferior de la pantalla. Un
 *   diálogo centrado deja las acciones donde no se llega; este las deja donde ya está la mano.
 *
 * RUTAS DE ESCAPE
 *   Se cierra con Esc, tocando el velo y con el botón *Cancelar* que le pase la pantalla. Al
 *   abrirse el foco entra en el panel y al cerrarse **vuelve a quien lo abrió**, para que el
 *   recorrido de teclado no se pierda. El tabulador da vueltas dentro mientras está abierto.
 *
 * POR QUÉ NO ES UN `<dialog>`
 *   `showModal()` daría el foco y el velo de balde, pero su comportamiento cambia entre
 *   navegadores y no está implementado en el DOM de las pruebas. Aquí el contrato se ve entero.
 */

/** Lo que puede recibir el foco dentro del panel. */
const FOCUSABLES =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Encabezado del panel; también es su nombre accesible. */
  title: string;
  children: ReactNode;
  /** Botonera inferior: normalmente *Cancelar* y la acción del panel. */
  footer?: ReactNode;
}

export function Sheet({ open, onClose, title, children, footer }: SheetProps) {
  const panel = useRef<HTMLDivElement>(null);
  const disparador = useRef<Element | null>(null);
  const tituloId = useId();

  const alPulsarTecla = useCallback(
    (evento: ReactKeyboardEvent<HTMLDivElement>) => {
      if (evento.key === "Escape") {
        evento.stopPropagation();
        onClose();
        return;
      }
      if (evento.key !== "Tab" || !panel.current) return;

      const focusables = Array.from(
        panel.current.querySelectorAll<HTMLElement>(FOCUSABLES),
      );
      if (focusables.length === 0) return;

      const primero = focusables[0];
      const ultimo = focusables[focusables.length - 1];
      const activo = document.activeElement;

      if (evento.shiftKey && activo === primero) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && activo === ultimo) {
        evento.preventDefault();
        primero.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return undefined;

    disparador.current = document.activeElement;
    const primero =
      panel.current?.querySelector<HTMLElement>(FOCUSABLES) ?? panel.current;
    primero?.focus();

    return () => {
      // Devolver el foco a quien abrió el panel es lo que evita la trampa de teclado.
      if (disparador.current instanceof HTMLElement) {
        disparador.current.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* El velo cierra con el dedo o el ratón. No es un objetivo de teclado a propósito: quien
          navega con teclado sale con Esc o con el botón del pie, y un elemento enfocable fuera
          del panel solo añadiría una parada de tabulador que no lleva a ninguna parte. */}
      <div
        aria-hidden="true"
        data-testid="sheet-overlay"
        onClick={onClose}
        className="absolute inset-0 bg-paper/70"
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        tabIndex={-1}
        onKeyDown={alPulsarTecla}
        className="pb-safe relative grid w-full max-w-content gap-3 rounded-t-2xl border border-b-0 border-border bg-surface p-3.5"
      >
        <div
          aria-hidden="true"
          className="h-1 w-9 justify-self-center rounded-full bg-border"
        />
        <h2 id={tituloId} className="text-lg normal-case tracking-normal">
          {title}
        </h2>
        {children}
        {footer ? <div className="grid grid-cols-2 gap-2">{footer}</div> : null}
      </div>
    </div>
  );
}
